import * as React from "react";
import {Context} from "react";
import {Permission} from "@/authSchema";
import {ContextFilter, useAuth} from "@/context/AuthProvider";
import {Link as RouterLink} from "react-router";
import {Link as MUILink, ListSubheader, MenuItem} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {FormattedMessage} from "react-intl";
import {numberTypeTranslations, zeroPad} from "@/tools";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {SyntheticListenerMap} from "@dnd-kit/core/dist/hooks/utilities";
import {DraggableAttributes} from "@dnd-kit/core";
import {NumberType} from "@/api";

export const HasPermission = (props: {name: Permission; contextFilter?: ContextFilter; children: React.ReactNode}) => {
    const auth = useAuth();
    return auth.hasPermission(props.name, props.contextFilter) ? (
        <React.Fragment>{props.children}</React.Fragment>
    ) : null;
};

type LinkProps = {
    to: string;
    children: React.ReactNode;
    openInNewTab?: boolean;
};

export const Link = ({to, children, openInNewTab = false}: LinkProps) => (
    <MUILink
        component={RouterLink}
        to={to}
        underline="hover"
        target={openInNewTab ? "_blank" : undefined}
        // for safety (https://mui.com/material-ui/react-link/#security)
        rel={openInNewTab ? "noopener noreferrer" : undefined}
    >
        {children}
    </MUILink>
);

export function createGroupedNumbersOptions(numbers: {numberId: number; name: string; numberType: NumberType}[]) {
    const items = [
        <MenuItem key="0" value={0}>
            <em>
                <FormattedMessage id="none" defaultMessage="None" />
            </em>
        </MenuItem>,
    ];

    if (!numbers?.length) return items;

    let prevType: NumberType | null = null;

    for (const item of numbers) {
        if (item.numberType && item.numberType !== prevType)
            items.push(
                <ListSubheader key={`type_${item.numberType}`}>
                    <FormattedMessage {...numberTypeTranslations[item.numberType]} />
                </ListSubheader>,
            );
        items.push(
            <MenuItem key={item.numberId} value={item.numberId} style={{paddingLeft: item.numberType ? "2em" : "1em"}}>
                {item.name}
            </MenuItem>,
        );
        prevType = item.numberType;
    }

    return items;
}

export const createContextProvider = (context: Context<any>, valueHook: () => any) => {
    const ContextProvider = context;

    return ({children}: {children: React.ReactNode}) => (
        <ContextProvider.Provider value={valueHook()}>{children}</ContextProvider.Provider>
    );
};

export const Duration = ({value}: {value: number}) => {
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value - hours * 3600) / 60);
    const seconds = Math.round(value - hours * 3600 - minutes * 60);

    return (
        <span>
            {hours ? `${hours}:` : ""}
            {zeroPad(minutes)}:{zeroPad(seconds)}
        </span>
    );
};

const useDragHandleStyles = makeStyles()({
    dragIcon: {
        cursor: "grab",
        "&: active": {
            cursor: "grabbing",
        },
    },
    disabled: {
        cursor: "default",
        userSelect: "none",
        pointerEvents: "none",
    },
});

type DragHandleProps = {
    setActivatorNodeRef: (element: HTMLElement) => void;
    listeners: SyntheticListenerMap;
    attributes: DraggableAttributes;
    disabled?: boolean;
    color?: string;
};

export const DragHandleIcon = ({setActivatorNodeRef, listeners, attributes, disabled, color}: DragHandleProps) => {
    const {classes} = useDragHandleStyles();
    return (
        <span
            className={disabled ? classes.disabled : classes.dragIcon}
            ref={setActivatorNodeRef}
            {...listeners}
            {...attributes}
        >
            <FontAwesomeIcon icon="ellipsis-v" color={color} />
        </span>
    );
};
